// =============================================================================
// SetupPointer — "this needs setting up first", said to the person reading it.
//
//   <SetupPointer target="loadingBayLanes">
//     {link => <>Add lanes in {link} first.</>}
//   </SetupPointer>
//
// A reader who can open Config gets the sentence with the place as a link
// (<ConfigLink/>, so the same gate, the same GuardedLink / new-tab rules). A
// reader who cannot — a loader, a scale operator — gets who does it instead:
// "A manager sets up loading-bay lanes: ask your manager." The sentence that
// told them to open a page they do not have, or a link that bounced them to
// Home, is gone (F19 UF-11).
//
// A section that renders only for managers and admins (Email groups) carries
// `minRole` on its target: a reader who opens Config but sits below that role
// gets the same "ask your manager" line — a link would land them on a tab
// without the section.
//
// Names ("… ask Thandi M or Pieter V.") only for a reader who may read the
// member list (useBusinessAdmins applies that gate); floor roles never get them.
//
// While the gate is still deciding, the sentence shows with the place as plain
// text — ConfigLink's own pending rendering — so nothing flips from a role
// line to a link.
//
// The targets and their words live in setupTargets.ts: add the setting there.
// =============================================================================

import type { ReactNode } from 'react'
import ConfigLink from '@/components/ConfigLink'
import { useCanOpenPackhouseConfig } from '@/hooks/useCanOpenPackhouseConfig'
import { useBusinessAdmins, ADMIN_ROLES, MANAGER_AND_ADMIN_ROLES } from '@/hooks/useBusinessAdmins'
import { useCurrentUser } from '@/lib/useCurrentUser'
import {
  SETUP_TARGETS, meetsMinRole, setupAskSentence, type SetupTarget, type SetupTargetKey,
} from '@/lib/setupTargets'

export interface SetupPointerProps {
  target: SetupTargetKey
  /** The sentence for a reader who can open Config; `link` is the place. */
  children: (link: ReactNode) => ReactNode
  /** Open in a new tab — inside a capture form or dialog (see ConfigLink). */
  newTab?: boolean
  /** Classes for the link (e.g. a colour the sentence carried). */
  linkClassName?: string
}

export default function SetupPointer({ target, children, newTab, linkClassName }: SetupPointerProps) {
  const t: SetupTarget = SETUP_TARGETS[target]
  const canOpen = useCanOpenPackhouseConfig()
  const { data: me } = useCurrentUser()
  // canOpen === true means the reader is known; below the section's role, the
  // tab would open without the section on it.
  const ask = canOpen === false || (canOpen === true && !meetsMinRole(t.minRole, me?.role))
  const names = useBusinessAdmins(t.who === 'admin' ? ADMIN_ROLES : MANAGER_AND_ADMIN_ROLES, ask)
  if (ask) return <>{setupAskSentence(t, names)}</>
  return <>{children(
    <ConfigLink tab={t.tab} section={t.section} newTab={newTab} className={linkClassName}>
      {t.place}
    </ConfigLink>,
  )}</>
}
